import { Temporal } from 'temporal-polyfill'
import { map, reduce, first, last, sortBy } from 'lodash-es'
import TimetoastTemporal from '@base/timetoast_temporal'
import type { HorizontalTimelineState } from '../reducers/index'
import { createHorizontalTimelineSelector as createSelector } from '../selectors'
import { DurationDefinition } from '../types/types'
import { DerivedMarker, DerivedTimespan } from '../types/derived_items'

export function getHorizontalVizBase(state: HorizontalTimelineState) {
  return state
}

const sortableObjFn = (timetoastTemporal: TimetoastTemporal): number => timetoastTemporal.getTime()

export const firstAndLastDateSelector: (
  state: HorizontalTimelineState,
  props: any
) => [TimetoastTemporal | null, TimetoastTemporal | null] = createSelector(
  (_state: HorizontalTimelineState, props: any) => props.markers,
  (_state: HorizontalTimelineState, props: any) => props.timespans,
  function (
    rawMarkersList: DerivedMarker[],
    rawTimespansList: DerivedTimespan[]
  ): [TimetoastTemporal | null, TimetoastTemporal | null] {
    const markersList = Array.isArray(rawMarkersList) ? rawMarkersList : []
    const timespansList = Array.isArray(rawTimespansList) ? rawTimespansList : []
    const numItems = markersList.length + timespansList.length
    if (numItems === 0) {
      return [null, null]
    } else if (numItems === 1) {
      if (markersList.length === 1) {
        const markerTemporal = TimetoastTemporal.parse(markersList[0].date)
        return [markerTemporal, markerTemporal]
      } else if (timespansList.length === 1) {
        const timespan = timespansList[0]
        const sortedDates = sortBy(
          [TimetoastTemporal.parse(timespan.start_date), TimetoastTemporal.parse(timespan.end_date)],
          [sortableObjFn]
        )
        // Ensure always returning a tuple of length 2
        return [sortedDates[0] ?? null, sortedDates[1] ?? null]
      }
    } else {
      const markerDatesList = map(markersList, marker => TimetoastTemporal.parse(marker.date))
      const timespanDatesList = reduce(
        timespansList,
        (result, value, _key) => {
          // Note: value here is a timespan
          result.push(TimetoastTemporal.parse(value.start_date))
          result.push(TimetoastTemporal.parse(value.end_date))
          return result
        },
        [] as TimetoastTemporal[]
      )
      const allDatesList = markerDatesList.concat(timespanDatesList)
      const sortedDatesList = sortBy(allDatesList, [sortableObjFn])
      // Ensure always returning a tuple of length 2
      return [first(sortedDatesList) ?? null, last(sortedDatesList) ?? null]
    }
    // Fallback, should never hit, but for type safety
    return [null, null]
  }
)

export const durationSelector: (state: HorizontalTimelineState, props: any) => DurationDefinition = createSelector(
  firstAndLastDateSelector,
  function (firstAndLastDate) {
    let startTemporal = first(firstAndLastDate)
    let endTemporal = last(firstAndLastDate)
    if (startTemporal && endTemporal) {
      let startTime = startTemporal.getTime()
      let endTime = endTemporal.getTime()
      let duration = endTime - startTime
      return {
        startTime,
        endTime,
        duration,
      }
    } else {
      const now = Temporal.Now.instant().epochMilliseconds
      return {
        startTime: now,
        endTime: now,
        duration: 0,
      }
    }
  }
)
